/**
 * Which staff screens care about which realtime events, and what each one has
 * to re-fetch when one arrives.
 *
 * ## Why this is two lists and not one
 *
 * These are two different vocabularies that happen to share words on the screen
 * where this table was first written.
 *
 * `watch` is the **server's** word. `Realtime::LocationBroadcaster` maps an
 * aggregate to the keys it invalidates -- `Location::Order` publishes
 * `orders bills tables dashboard` -- and an incoming message is only interesting
 * if one of those keys is in this list.
 *
 * `reload` is the **client's** word: the Inertia prop name the page wants
 * refreshed, passed straight to `router.reload({ only })`.
 *
 * On `/orders` both are `orders`, so a single list served both readers and the
 * conflation was invisible. `/kds` is where they come apart: a station ticket IS
 * a `Location::Order`, so the board must watch `orders` -- but
 * `Staff::Location::KdsController#show` sends a prop called `tickets`, so that
 * is what has to be reloaded. One list cannot say both, and whichever word it
 * held, the board would break: `tickets` matches no broadcast, `orders` reloads
 * a prop the page never received.
 */
export type RealtimeRoute = {
  /** Invalidation keys, in the broadcaster's vocabulary, worth reacting to. */
  watch: string[]
  /** Inertia prop names to re-fetch, in the page's vocabulary. */
  reload: string[]
}

const NOTHING: RealtimeRoute = { watch: [], reload: [] }

export function realtimeRouteFor(path: string): RealtimeRoute {
  if (path.startsWith("/orders")) {
    return { watch: ["orders"], reload: ["orders"] }
  }

  // The station board. Watches the order stream, reloads the board.
  //
  // Only `tickets` is reloaded, never the whole page: `station` and `stations`
  // are settled the moment the kiosk URL is opened, and re-fetching them would
  // rebuild the station nav under the hand of someone mid-tap.
  if (path.startsWith("/kds")) {
    return { watch: ["orders"], reload: ["tickets"] }
  }

  // `pagination` rides along in `reload` but deliberately not in `watch`: no
  // broadcast has ever carried that word, so watching for it could only ever
  // match nothing.
  if (path.startsWith("/bills")) {
    return { watch: ["bills"], reload: ["bills", "pagination"] }
  }

  if (path.startsWith("/tables")) {
    return { watch: ["tables"], reload: ["tables"] }
  }

  if (path === "/" || path.startsWith("/dashboard")) {
    return { watch: ["dashboard"], reload: ["dashboard"] }
  }

  return NOTHING
}
